/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import type { LinkProps, Theme } from '@coursera/cds-core';
import { Link, useTheme } from '@coursera/cds-core';

import { EnrollButton } from 'bundles/clips/components/EnrollButton';
import { PartnerLogo } from 'bundles/clips/components/PartnerLogo';
import type { ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import type { ClipsCourseDetailsQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember_cdpMetadata_partners as Partner } from 'bundles/clips/components/short-form-content/__generated__/ClipsCourseDetailsQuery';
import type { Course, OverridePartnerLogos } from 'bundles/clips/types/vlpSharedTypes';
import { getProgramHomeMiniXdpLink } from 'bundles/clips/utils/clipsUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandVideosV1 from 'bundles/naptimejs/resources/onDemandVideos.v1';

import _t from 'i18n!nls/clips';

type PropsFromCaller = {
  partners: Array<Partner> | null;
  overridePartnerLogos?: OverridePartnerLogos;
  course: Course;
  accessProvidedViaProgram?: ProgramSuggestion;
  onDemandVideos: OnDemandVideosV1;
  justEnrolledThroughMutation?: boolean;
  userEnrolledIntoCourse?: boolean;
  itemId: string;
  setShowEnrollmentButtonError: (error: boolean) => void;
};

export type Props = PropsFromCaller;

const courseContentCardContainerMobile = (theme: Theme) =>
  css({
    borderTop: `2px solid var(--cds-color-grey-50)`,
    padding: '8px 16px 8px 24px',
    background: 'var(--cds-color-grey-25)',
    position: 'fixed',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: '999',
    marginLeft: '-16px',
    marginRight: '-16px',
    width: '100%',
  });

const courseMetadataMobile = css({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});
const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Link
);
export const MobileCourseCard = ({
  partners,
  overridePartnerLogos,
  course,
  accessProvidedViaProgram,
  onDemandVideos,
  justEnrolledThroughMutation,
  userEnrolledIntoCourse,
  itemId,
  setShowEnrollmentButtonError,
}: Props) => {
  const theme = useTheme();
  const partner = partners && partners[0];

  let partnerId;
  let partnerSquareLogo;
  let logoSrc;

  if (partner) {
    partnerId = partner.id;
    partnerSquareLogo = partner.squareLogo;
    logoSrc = (overridePartnerLogos && overridePartnerLogos[partnerId]) || partnerSquareLogo;
  }

  return (
    <div css={courseContentCardContainerMobile(theme)} data-testid="course-content-card-mobile">
      <div css={courseMetadataMobile}>
        {partner && logoSrc && <PartnerLogo logoSrc={logoSrc} partnerId={partner.id} partnerName={partner.name} />}
        <TrackedLink
          variant="quiet"
          typographyVariant="subtitleMedium"
          component={ReactRouterLink}
          target="_blank"
          trackingName="short_form_content_vlp_course_link"
          to={getProgramHomeMiniXdpLink({
            courseId: course.id,
            accessProvidedViaProgram,
            trackingName: 'sfc_page_course_link',
            itemId,
          })}
        >
          {course.name}
        </TrackedLink>
      </div>
      <div>
        <EnrollButton
          enrollText={_t('Enroll')}
          goToCourseText={_t('Go to course')}
          justEnrolledThroughMutation={justEnrolledThroughMutation}
          userEnrolledIntoCourse={userEnrolledIntoCourse}
          course={course}
          itemId={itemId}
          programId={accessProvidedViaProgram?.id}
          setShowEnrollmentButtonError={setShowEnrollmentButtonError}
        />
      </div>
    </div>
  );
};
